// FormDialog — modal form shell with standard + wizard modes (CHG-1233 / pack 2).
//
// Standard mode: header + fields body + footer (Cancel/Submit).
// Wizard mode (type="wizard"): stepper across top, current step's fields,
//                              footer Back/Skip/Next (Submit on last step).
//
// Field rendering goes through @fields/registry — `field.type` resolves to a
// canonical primitive. Hand-rolled <input> in form bodies is the smell that
// feedback_field_primitive_components_canonical.md prohibits.
//
// Per feedback_wizard_e2e_must_compare_consumers.md (per brief v5b): emits a
// single <form> element in BOTH modes so e2e assertions don't have to branch.
// Per feedback_composition_system.md: slot composition, values are controlled
// by the parent — no internal value state. The only internal state is the
// wizard step index.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { X } from "lucide-react";
import { fieldRegistry, type FieldType } from "@fields/registry";
import { FormDialogStepper } from "./FormDialogStepper";
// CHG-1271 — surface 4: brand logo in wizard mode header. Pure useBrand
// consumer; no new props, no API change to FormDialog.
import { useBrand } from "../../contexts/BrandContext";

export interface FormFieldDef {
  name: string;
  type: FieldType;
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  /** Extra props forwarded to the underlying field primitive. */
  props?: Record<string, unknown>;
}

export interface WizardStep {
  key: string;
  title: string;
  fields: FormFieldDef[];
  optional?: boolean;
}

export type FormDialogSize = "sm" | "md" | "lg";

interface BaseProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  onSubmit: (values: Record<string, unknown>) => Promise<void> | void;
  submitLabel?: string;
  cancelLabel?: string;
  size?: FormDialogSize;
  busy?: boolean;
  /** Form-wide error banner (top of the body). */
  error?: string;
  /**
   * Per-field validation errors keyed by field `name` (DRIFT-942 / DRIFT-912).
   * Forwarded as the `error` prop of the matching field primitive, which
   * surfaces it via role="alert" + aria-invalid + aria-describedby (the §2 a11y
   * branch that was dead at the composition level before this). Callers map a
   * backend 422 `field_errors[]` (or client-side validation) into this record —
   * see lib/parseFieldErrors.ts. Converges the v2 API toward v1's per-field
   * error capability.
   */
  errors?: Record<string, string>;
}

interface StandardProps extends BaseProps {
  type?: "standard";
  fields: FormFieldDef[];
}

interface WizardProps extends BaseProps {
  type: "wizard";
  steps: WizardStep[];
}

export type FormDialogProps = StandardProps | WizardProps;

const WIDTH: Record<FormDialogSize, number> = { sm: 420, md: 560, lg: 760 };

function isWizard(props: FormDialogProps): props is WizardProps {
  return props.type === "wizard";
}

export function FormDialog(props: FormDialogProps) {
  const { open, onClose, title, description, values, onChange, onSubmit, busy, error, errors, size = "md" } = props;
  const wizard = isWizard(props);
  const { chrome } = useBrand();
  const titleId = useId();
  const descId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const totalSteps = wizard ? props.steps.length : 1;
  const currentFields: FormFieldDef[] = wizard ? props.steps[stepIndex]?.fields ?? [] : props.fields;
  const currentStep: WizardStep | undefined = wizard ? props.steps[stepIndex] : undefined;
  const isLastStep = !wizard || stepIndex === totalSteps - 1;
  const isFirstStep = stepIndex === 0;

  // Reset step index whenever the dialog opens fresh.
  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  // Escape closes the dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Initial focus on first focusable input inside the form.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      const first = formRef.current?.querySelector<HTMLElement>("input,select,textarea,button");
      first?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, stepIndex]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (busy) return;
      if (wizard && !isLastStep) {
        setStepIndex((i) => Math.min(i + 1, totalSteps - 1));
        return;
      }
      await onSubmit(values);
    },
    [busy, wizard, isLastStep, totalSteps, onSubmit, values],
  );

  const submitText = useMemo(() => {
    if (wizard && !isLastStep) return "Next";
    return props.submitLabel ?? "Save";
  }, [wizard, isLastStep, props.submitLabel]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-lg, 12px)",
          boxShadow: "var(--shadow-overlay, 0 25px 50px -12px rgba(0,0,0,0.25))",
          width: "100%",
          maxWidth: WIDTH[size],
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "16px 20px 12px",
            borderBottom: "1px solid var(--color-border-subtle)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {/* CHG-1271 — surface 4: brand logo in wizard-mode header.
                Renders only when type="wizard" so standard dialogs stay
                visually unchanged. */}
            {wizard ? (
              <img
                src={chrome.logo_url}
                alt=""
                aria-hidden="true"
                data-brand-logo
                style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0 }}
              />
            ) : null}
            <div style={{ minWidth: 0 }}>
            <h2
              id={titleId}
              className="font-display"
              style={{ margin: 0, fontSize: 18, fontWeight: 500, color: "var(--color-text)" }}
            >
              {title}
            </h2>
            {description ? (
              <div id={descId} style={{ marginTop: 4, fontSize: 12, color: "var(--color-text-muted)" }}>
                {description}
              </div>
            ) : null}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="btn btn-ghost"
            style={{ width: 28, height: 28, padding: 0 }}
          >
            <X aria-hidden="true" size={14} />
          </button>
        </header>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          {wizard ? <FormDialogStepper steps={props.steps} currentIndex={stepIndex} /> : null}

          <div
            data-form-body
            style={{
              padding: "16px 20px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 14,
              flex: 1,
              minHeight: 0,
            }}
          >
            {error ? (
              <div
                role="alert"
                style={{
                  padding: "8px 12px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--color-error-tint)",
                  color: "var(--color-error)",
                  fontSize: 12,
                }}
              >
                {error}
              </div>
            ) : null}
            {currentFields.map((field) => {
              const Field = fieldRegistry[field.type];
              if (!Field) {
                return (
                  <div key={field.name} role="alert" style={{ fontSize: 12, color: "var(--color-error)" }}>
                    Unknown field type: <code>{field.type}</code>
                  </div>
                );
              }
              return (
                <Field
                  key={field.name}
                  name={field.name}
                  value={values[field.name] ?? null}
                  onChange={(v: unknown) => onChange(field.name, v)}
                  label={field.label}
                  helperText={field.helperText}
                  required={field.required}
                  disabled={field.disabled || busy}
                  error={errors?.[field.name]}
                  {...(field.props ?? {})}
                />
              );
            })}
          </div>

          <footer
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
              padding: "12px 20px",
              borderTop: "1px solid var(--color-border-subtle)",
            }}
          >
            {wizard && !isFirstStep ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                disabled={busy}
              >
                Back
              </button>
            ) : null}
            {wizard && !isLastStep && currentStep?.optional ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setStepIndex((i) => Math.min(totalSteps - 1, i + 1))}
                disabled={busy}
              >
                Skip
              </button>
            ) : null}
            <span style={{ flex: 1 }} />
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              {props.cancelLabel ?? "Cancel"}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Working…" : submitText}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
